import { Form, Formik, FormikProps, FormikValues } from "formik";
import { Children, ReactNode, useEffect } from "react";
import { Alert, Button, Modal, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { StyledModalTitle } from "../../@ui/StyledModalTitle";
import { Layer } from "../../types/Layer";
import {
  WfsBadRequestResponse,
  WfsDataResponse,
  WfsDeferredRequestResponse,
  WfsDuplicateOrderResponse,
  WfsPaymentRequiredResponse,
  WfsResponseType,
} from "../../types/Wfs/WfsResponse";
import { useDataSelectionContext } from "./DataSelectionContext";
import {
  BadRequestView,
  DataResultView,
  DeferredRequestView,
  PaymentRequiredView,
} from "./DataSelectionModalChildViews";
import { useApiContext } from "../api/ApiContext";
import { useAuthContext } from "../auth/AuthContext";
import ThemedModal from "../../@ui/Modal/ThemedModal";
import DuplicateOrderView from "./DataSelectionModalChildViews/DuplicateOrderView";

export interface DataSelectionModelProps<D> {
  headerText: string;
  layer: Layer | undefined;
  initialValues: FormikValues & D;
  validationSchema?: any;
  onSubmit: (values: D) => void;
  children?: ReactNode | ((props: FormikProps<FormikValues & D>) => ReactNode);
  size?: "sm" | "lg" | "xl";
  enableReinitialize?: boolean;
}

const DataSelectionModel = <D,>({
  headerText,
  layer,
  initialValues,
  validationSchema,
  onSubmit,
  children,
  size,
  enableReinitialize,
}: DataSelectionModelProps<D>) => {
  const { boundary: boundaryParam } = useParams();
  const navigate = useNavigate();
  const { hasBoundaries, wfsResult, wfsReset, loading, layers } =
    useDataSelectionContext();

  useEffect(() => {
    if (layers.length > 0 && !layer) navigate("/");
  }, [layer, layers, navigate]);
  const { abort } = useApiContext();
  const { user } = useAuthContext();

  const isEmptyChildren = (children: any): boolean =>
    Children.count(children) === 0;

  const isFunction = (obj: any): obj is Function => typeof obj === "function";

  const renderChildren = (
    children: ReactNode | ((props: FormikProps<FormikValues & D>) => ReactNode),
    formikBag: FormikProps<FormikValues & D>,
  ) => {
    if (isFunction(children)) {
      return (
        children as (bag: FormikProps<FormikValues & D>) => React.ReactNode
      )(formikBag);
    } else if (!isEmptyChildren(children)) {
      return Children.only(children);
    }

    return null;
  };

  const cancel = () => {
    if (loading) abort();
    navigate("/");
  };

  return (
    <>
      <ThemedModal
        centered
        show={!hasBoundaries && !boundaryParam}
        onHide={() => navigate("/")}
        backdrop="static">
        <Modal.Header className="text-white bg-secondary" closeButton>
          <StyledModalTitle>No Areas Selected</StyledModalTitle>
        </Modal.Header>
        <Modal.Body>
          Please select one or more areas before selecting data.
        </Modal.Body>
      </ThemedModal>

      <ThemedModal centered show={hasBoundaries} backdrop="static" size={size}>
        <Modal.Header className="bg-secondary text-white">
          <StyledModalTitle>{headerText}</StyledModalTitle>
        </Modal.Header>
        {!wfsResult && (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            validateOnMount
            validateOnChange
            enableReinitialize={enableReinitialize}>
            {(formikBag) => (
              <Form>
                <Modal.Body>
                  {layer?.isPremium && (
                    <Alert variant="warning">
                      This is a Premium dataset and will require additional
                      payment.
                    </Alert>
                  )}
                  {renderChildren(children, formikBag)}
                </Modal.Body>
                <Modal.Footer>
                  {loading && (
                    <p>Checking for data - this may take several minutes</p>
                  )}
                  {layer?.isPremium && !user && (
                    <p className="w-100 text-muted small text-end">
                      You will need to re-select your order parameters when you
                      login to buy the data.
                    </p>
                  )}
                  <Button
                    variant="primary"
                    onClick={() => formikBag.handleSubmit()}
                    disabled={!formikBag.isValid || loading}>
                    {layer?.isPremium && <>Get Quote</>}
                    {!layer?.isPremium && <>Order Data</>}{" "}
                    {loading && <Spinner size="sm" />}
                  </Button>
                  <Button variant="danger" onClick={cancel}>
                    Cancel
                  </Button>
                </Modal.Footer>
              </Form>
            )}
          </Formik>
        )}
        {wfsResult && wfsResult.type === WfsResponseType.Data && (
          <DataResultView
            onClose={() => wfsReset()}
            dataResponse={wfsResult as WfsDataResponse}
          />
        )}
        {wfsResult && wfsResult.type === WfsResponseType.BadRequest && (
          <BadRequestView
            badRequest={wfsResult as WfsBadRequestResponse}
            reset={wfsReset}
          />
        )}
        {wfsResult && wfsResult.type === WfsResponseType.DeferredRequest && (
          <DeferredRequestView
            deferredRequest={wfsResult as WfsDeferredRequestResponse}
            reset={wfsReset}
          />
        )}
        {wfsResult && wfsResult.type === WfsResponseType.PaymentRequired && (
          <PaymentRequiredView
            paymentRequired={wfsResult as WfsPaymentRequiredResponse}
          />
        )}
        {wfsResult && wfsResult.type === WfsResponseType.DuplicateOrder && (
          <DuplicateOrderView
            duplicateOrder={wfsResult as WfsDuplicateOrderResponse}
          />
        )}
      </ThemedModal>
    </>
  );
};

export default DataSelectionModel;
